import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Spinner } from "@/components/common";
import { SignaturePad } from "@/components/SignaturePad";
import { X, Eraser, RotateCcw, PenLine, ShieldCheck } from "lucide-react";

const ACEPTACION = "Declaro que revisé el documento completo, que la firma manuscrita que dibujé es mía, y autorizo su uso como firma electrónica únicamente para este documento, conforme a la Ley 527 de 1999 y el Decreto 2364 de 2012. Entiendo que mi firma no se reutilizará en otros documentos sin mi autorización.";

function PreguntaSST({ resp, setResp, avisado, avisar, avisando }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="firma-sst-pregunta">
      <p className="mb-2 text-sm text-slate-700">¿Conoces y tienes claras estas responsabilidades? *</p>
      <div className="flex gap-5">
        {["SI", "NO"].map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm"><input type="radio" name="sst-resp" checked={resp === o} onChange={() => setResp(o)} className="h-4 w-4 accent-vino-700" data-testid={`firma-sst-${o.toLowerCase()}`} /> {o}</label>
        ))}
      </div>
      {resp === "NO" && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-amber-700" data-testid="firma-sst-no-aviso">No puedes firmar si marcas NO. Avisa a HUMAN para que te expliquen las responsabilidades y luego firma marcando SI.</p>
          <button type="button" onClick={avisar} disabled={avisado || avisando} data-testid="firma-sst-avisar" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-60">{avisado ? "HUMAN ya fue avisado" : "Avisar a HUMAN"}</button>
        </div>
      )}
    </div>
  );
}

export default function FirmaManuscritaModal({ doc, hashVisto, yaAvisado, onClose, onSigned }) {
  const pad = useRef(null);
  const [vacio, setVacio] = useState(true);
  const [paso, setPaso] = useState("dibujar");
  const [img, setImg] = useState("");
  const [ident, setIdent] = useState("");
  const [acepta, setAcepta] = useState(false);
  const [sensibles, setSensibles] = useState(false);
  const pideSensibles = doc.template_key === "autorizacion_datos";
  const [resp, setResp] = useState("");
  const [saving, setSaving] = useState(false);
  const [avisado, setAvisado] = useState(!!yaAvisado);
  const [avisando, setAvisando] = useState(false);
  const pideSiNo = doc.template_key === "sgsst";

  const avisar = async () => {
    setAvisando(true);
    try { await api.post(`/portal/contratacion/documentos/${doc.id}/sst-no`); setAvisado(true); toast.success("Le avisamos a HUMAN. Te contactarán para explicarte las responsabilidades."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAvisando(false); }
  };
  const continuar = () => { setImg(pad.current.imagen()); setPaso("confirmar"); };
  const volver = () => { setImg(""); setAcepta(false); setPaso("dibujar"); };
  const confirmar = async () => {
    setSaving(true);
    try {
      await api.post(`/portal/contratacion/documentos/${doc.id}/firmar`, { trazo: img, identificacion: ident, hash_visto: hashVisto, acepta, acepta_sensibles: sensibles, respuesta_sst: pideSiNo ? resp : null });
      toast.success("Documento firmado");
      onSigned();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center sm:p-4" data-testid="firma-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 max-h-[95vh] w-full max-w-xl overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl sm:p-6">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Firmar documento</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="firma-cerrar"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-4 text-sm text-slate-500"><strong>{doc.nombre}</strong> · versión {doc.version || 1}</p>
        {paso === "dibujar" ? (
          <div className="space-y-3">
            <SignaturePad ref={pad} onChange={setVacio} />
            <div className="flex flex-wrap justify-between gap-2">
              <button onClick={() => pad.current.limpiar()} disabled={vacio} data-testid="firma-borrar" className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"><Eraser className="h-4 w-4" /> Borrar firma</button>
              <button onClick={continuar} disabled={vacio} data-testid="firma-continuar" className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50"><PenLine className="h-4 w-4" /> Usar esta firma</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex h-32 items-center justify-center rounded-xl border border-slate-200 bg-slate-50" data-testid="firma-vista-previa"><img src={img} alt="Tu firma" className="max-h-28 max-w-full" /></div>
            {pideSiNo && <PreguntaSST resp={resp} setResp={setResp} avisado={avisado} avisar={avisar} avisando={avisando} />}
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Confirma tu número de identificación *</label>
              <input inputMode="numeric" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-500" value={ident} onChange={(e) => setIdent(e.target.value)} data-testid="firma-identificacion" />
            </div>
            {pideSensibles && <label className="flex items-start gap-2 rounded-lg border border-vino-100 bg-vino-50/50 p-3 text-xs leading-relaxed text-slate-700"><input type="checkbox" checked={sensibles} onChange={(e) => setSensibles(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-vino-700" data-testid="firma-acepta-sensibles" /> Autorizo de manera expresa, libre y diferenciada el tratamiento de mis datos sensibles (salud, afiliación a EPS, AFP, ARL y CCF, pertenencia a sindicatos u organizaciones sociales, vida sexual y datos biométricos) y, cuando aplique, los de mis hijos menores de edad, para las finalidades descritas en el documento (Ley 1581 de 2012).</label>}
            <label className="flex items-start gap-2 text-xs leading-relaxed text-slate-600"><input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-vino-700" data-testid="firma-acepta" /> {ACEPTACION}</label>
            <p className="flex items-center gap-1.5 text-[11px] text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Se registrarán fecha, hora, IP y la huella SHA-256 del documento.</p>
            <div className="flex flex-wrap justify-between gap-2">
              <button onClick={volver} disabled={saving} data-testid="firma-volver" className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><RotateCcw className="h-4 w-4" /> Volver a firmar</button>
              <button onClick={confirmar} disabled={saving || !acepta || !ident.trim() || (pideSiNo && resp !== "SI") || (pideSensibles && !sensibles)} data-testid="firma-confirmar" className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50">{saving ? <Spinner /> : <PenLine className="h-4 w-4" />} Confirmar firma</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
